/**
 * LeftSidebar - Create Tools Panel
 *
 * BYK-157: Left sidebar for the fixed panel layout
 * Contains creation tools organized in tabs:
 * - Products: Browse and place products
 * - Layouts: Quick-start layout templates
 * - Styles: Style palette selection
 */

import { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { ProductCatalog } from "./ProductCatalog";
import { LayoutTemplates } from "./LayoutTemplates";
import { StylePaletteSelector } from "./StylePaletteSelector";
import { useFloorPlanStore } from "@/stores/floorPlan";
import {
  Package,
  LayoutGrid,
  Palette,
  ChevronDown,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

type TabId = "products" | "layouts" | "styles";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  shortLabel: string;
}

// ============================================================================
// Constants
// ============================================================================

const TABS: Tab[] = [
  {
    id: "products",
    label: "Products",
    shortLabel: "Products",
    icon: <Package className="h-4 w-4" />,
  },
  {
    id: "layouts",
    label: "Layouts",
    shortLabel: "Layouts",
    icon: <LayoutGrid className="h-4 w-4" />,
  },
  {
    id: "styles",
    label: "Styles",
    shortLabel: "Styles",
    icon: <Palette className="h-4 w-4" />,
  },
];

// ============================================================================
// Tab Button Component
// ============================================================================

interface TabButtonProps {
  tab: Tab;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ tab, isActive, onClick }: TabButtonProps) {
  return (
    <button
      id={`left-tab-${tab.id}`}
      onClick={onClick}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 px-3 py-2",
        "text-sm font-medium transition-all duration-150",
        "rounded-lg",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset",
        isActive
          ? "bg-blue-50 text-blue-600"
          : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      )}
      aria-selected={isActive}
      aria-controls={`left-tabpanel-${tab.id}`}
      role="tab"
      tabIndex={isActive ? 0 : -1}
    >
      {tab.icon}
      <span className="hidden sm:inline">{tab.shortLabel}</span>
    </button>
  );
}

// ============================================================================
// Tab Content Component
// ============================================================================

interface TabContentProps {
  activeTab: TabId;
}

function TabContent({ activeTab }: TabContentProps) {
  const { setStylePalette } = useFloorPlanStore();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeTab}
        id={`left-tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`left-tab-${activeTab}`}
        initial={{ opacity: 0, x: -10, pointerEvents: "none" as const }}
        animate={{ opacity: 1, x: 0, pointerEvents: "auto" as const }}
        exit={{ opacity: 0, x: 10, pointerEvents: "none" as const }}
        transition={{ duration: 0.15 }}
        className="h-full"
        tabIndex={0}
      >
        {activeTab === "products" && <ProductCatalog />}
        {activeTab === "layouts" && (
          <div className="h-full overflow-y-auto p-3" style={{ overscrollBehaviorY: 'contain' }}>
            <LayoutTemplates />
          </div>
        )}
        {activeTab === "styles" && (
          <div className="h-full overflow-y-auto p-3" style={{ overscrollBehaviorY: 'contain' }}>
            <StylePaletteSelector onApply={setStylePalette} />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================================
// Collapsible Section Component (Alternative Layout)
// ============================================================================

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({
  title,
  icon,
  isExpanded,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={onToggle}
        className={cn(
          "flex w-full items-center justify-between px-4 py-3",
          "text-sm font-medium text-gray-700",
          "hover:bg-gray-50 transition-colors"
        )}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// Left Sidebar Component
// ============================================================================

export interface LeftSidebarProps {
  /** Layout mode: 'tabs' for tabbed interface, 'accordion' for collapsible sections */
  layout?: "tabs" | "accordion";
  /** Custom class name */
  className?: string;
}

export function LeftSidebar({
  layout = "tabs",
  className,
}: LeftSidebarProps) {
  const [activeTab, setActiveTab] = useState<TabId>("products");
  const [expandedSections, setExpandedSections] = useState<Set<TabId>>(
    new Set(["products"])
  );
  const { setStylePalette } = useFloorPlanStore();
  const tabListRef = useRef<HTMLDivElement>(null);

  const toggleSection = useCallback((sectionId: TabId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Arrow key navigation for roving tabindex (WCAG 2.1.1)
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);
      let newIndex = currentIndex;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          newIndex = currentIndex === 0 ? TABS.length - 1 : currentIndex - 1;
          break;
        case "ArrowRight":
          e.preventDefault();
          newIndex = currentIndex === TABS.length - 1 ? 0 : currentIndex + 1;
          break;
        case "Home":
          e.preventDefault();
          newIndex = 0;
          break;
        case "End":
          e.preventDefault();
          newIndex = TABS.length - 1;
          break;
        default:
          return;
      }

      const newTab = TABS[newIndex];
      setActiveTab(newTab.id);

      // Focus the new tab button
      const tabButton = tabListRef.current?.querySelector(
        `#left-tab-${newTab.id}`
      ) as HTMLButtonElement | null;
      tabButton?.focus();
    },
    [activeTab]
  );

  if (layout === "accordion") {
    return (
      <div className={cn("flex h-full flex-col bg-white", className)}>
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-800">Create</h2>
          <p className="text-xs text-gray-500">Add elements to your design</p>
        </div>

        {/* Accordion Sections */}
        <div className="flex-1 overflow-y-auto" style={{ overscrollBehaviorY: 'contain' }}>
          <CollapsibleSection
            title="Products"
            icon={<Package className="h-4 w-4" />}
            isExpanded={expandedSections.has("products")}
            onToggle={() => toggleSection("products")}
          >
            <div className="max-h-[400px] overflow-y-auto">
              <ProductCatalog />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Layout Templates"
            icon={<LayoutGrid className="h-4 w-4" />}
            isExpanded={expandedSections.has("layouts")}
            onToggle={() => toggleSection("layouts")}
          >
            <div className="p-3">
              <LayoutTemplates />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Style Palette"
            icon={<Palette className="h-4 w-4" />}
            isExpanded={expandedSections.has("styles")}
            onToggle={() => toggleSection("styles")}
          >
            <div className="p-3">
              <StylePaletteSelector onApply={setStylePalette} />
            </div>
          </CollapsibleSection>
        </div>
      </div>
    );
  }

  // Default: Tabbed layout
  return (
    <nav
      id="left-sidebar"
      aria-label="Create tools sidebar"
      className={cn("flex h-full flex-col bg-white", className)}
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-800">Create</h2>
        <p className="text-xs text-gray-500">Add elements to your design</p>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b border-gray-200 p-2">
        <div
          ref={tabListRef}
          className="flex gap-1 rounded-lg bg-gray-100 p-1"
          role="tablist"
          aria-label="Create tools navigation"
          onKeyDown={handleTabKeyDown}
        >
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        <TabContent activeTab={activeTab} />
      </div>
    </nav>
  );
}

export default LeftSidebar;
